<template>
  <div class="child-record">
    <div class="child-record-header" @click="toggleExpanded">
      <button class="arrow-button">
        {{ isExpanded ? '▾' : '▸' }}
      </button>
      <span class="record-type">Detainee Callback Message</span>
      
      <div v-if="record.showAlert" class="alert-message" :class="{ 'status-completed': record.status === 'completed' }">
        <span class="alert-text">{{ record.alert }}</span>
        <span class="alert-dismiss" @click.stop="dismissAlert">&times;</span>
      </div>

      <div class="timestamps">
        <div class="timestamp-line">Created: {{ formatDetailedTimestamp(record.timestamp) }}</div>
        <div v-if="record.deliveryDate" class="timestamp-line">
          Message Received: {{ formatDetailedTimestamp(record.deliveryDate) }}
        </div>
      </div>

      <span v-if="record.flag" class="flag-icon">⚑</span>

      <button class="kebab-menu" @click.stop="toggleKebabMenu">⋮</button>
      <div v-if="showKebabMenu" class="kebab-menu-dropdown">
        <button @click.stop="toggleAlert">
          Show / Hide Alert
        </button>
        <button @click.stop="toggleFlag">
          Flag / Unflag
        </button>
      </div>
    </div>

    <div v-if="isExpanded" class="expanded-content">
      <div class="message-section">
        <!-- Message Header -->
        <div class="text-h6 font-weight-bold" style="color: #333333;">
          When the detainee calls back, say this message . . .
        </div>

        <!-- Message Content -->
        <div class="ms-8 mt-4">
          <!-- Edit Mode -->
          <div v-if="isEditing" class="d-flex flex-column">
            <v-row no-gutters>
              <v-col cols="11">
                <v-textarea
                  v-model="editingText"
                  density="compact"
                  variant="outlined"
                  hide-details
                  :rows="4"
                  class="flex-grow-1"
                  @keyup.esc="cancelEdit"
                  ref="messageInput"
                ></v-textarea>
              </v-col>
              <v-col cols="1" class="d-flex justify-center">
                <div class="d-flex flex-column ms-2">
                  <v-icon 
                    color="success" 
                    size="small"
                    class="mb-2"
                    :class="{ 'spinning': isSaving }"
                    :disabled="isSaving"
                    @click="saveMessage"
                  >
                    {{ isSaving ? 'mdi-loading' : 'mdi-check' }}
                  </v-icon>
                  <v-icon 
                    color="error" 
                    size="small"
                    @click="cancelEdit"
                  >
                    mdi-close
                  </v-icon>
                </div>
              </v-col>
            </v-row>
          </div>

          <!-- View Mode -->
          <v-row v-else no-gutters>
            <v-col cols="11">
              <div class="message-text">{{ record.message["en-US"] || 'No message set' }}</div>
            </v-col>
            <v-col cols="1" class="d-flex justify-center">
              <v-icon 
                v-if="!record.deliveryDate"
                class="ms-2 edit-icon" 
                size="small"
                @click="startEdit"
              >
                mdi-pencil
              </v-icon>
            </v-col>
          </v-row>
        </div>

        <!-- Action Selection -->
        <div class="text-h6 font-weight-bold mt-6" style="color: #333333;">
          AND...
        </div>
        
        <div class="radio-group mt-4 ms-8">
          <v-radio-group 
            v-model="selectedAction"
            @update:model-value="handleActionChange"
            :disabled="!!record.deliveryDate"
          >
            <v-radio 
              v-for="(label, value) in actionLabels" 
              :key="value"
              :value="value"
            >
              <template v-slot:label>
                <div class="forward-call-container" style="font-size: 17px; font-weight: bold; color: #000000;">
                  <span :class="{ 'forward-call-label': value === 'FORWARD_CALL' }">{{ label }}</span>
                  <!-- Phone number input for FORWARD_CALL -->
                  <v-text-field
                    v-if="value === 'FORWARD_CALL'"
                    :model-value="formatPhoneNumber(record.detaineeForwardNumber)"
                    placeholder="###-###-####"
                    readonly
                    density="compact"
                    variant="outlined"
                    hide-details
                    class="ml-2 phone-input"
                    :disabled="!!record.deliveryDate"
                    @click="openPhoneDialog"
                  ></v-text-field>
                </div>
              </template>
            </v-radio>
          </v-radio-group>
        </div>

        <!-- Detainee Response Section -->
        <div v-if="record.detaineeMessage" class="mt-6">
          <div class="text-h6 font-weight-bold" style="color: #333333;">
            Detainee Response:
          </div>
          <div class="mt-4 ms-8 pl-2">
            {{ record.detaineeMessage["en-US"] }}
          </div>
        </div>
        <div v-if="record.detaineeForwardResultString" class="mt-6">
          <div class="text-h6 font-weight-bold" style="color: #333333;">
            Detainee Response:
          </div>
          <div class="mt-4 ms-8 pl-2">
            {{ record.detaineeForwardResultString }}
          </div>
        </div>
        <div v-if="record.detaineePlayOnlyResultString" class="mt-6">
          <div class="text-h6 font-weight-bold" style="color: #333333;">
            Detainee Response:
          </div>
          <div class="mt-4 ms-8 pl-2">
            {{ record.detaineePlayOnlyResultString }}
          </div>
        </div>
      </div>
      <div v-if="record.detaineeAudio" class="recording-section" style="margin-left: 26px;">
        <audio controls class="audio-player">
          <source :src="record.detaineeAudio" type="audio/wav">
          Your browser does not support the audio element.
        </audio>
      </div>
    </div>

    <!-- Phone Number Dialog -->
    <v-dialog 
      v-model="showPhoneDialog" 
      max-width="400px"
      persistent
    >
      <v-card>
        <v-card-title class="text-h6">
          Phone Number
        </v-card-title>
        <v-card-text>
          <div class="mb-3">Enter a ten-digit phone number in any format:</div>
          <v-text-field
            v-model="phoneDialogValue"
            placeholder="###-###-####"
            variant="outlined"
            hide-details
            :error="phoneValidationError"
            @keyup.enter="validateAndSavePhone"
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="cancelPhoneDialog">
            Cancel
          </v-btn>
          <v-btn color="primary" @click="validateAndSavePhone">
            OK
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'DetaineeCallbackRecord',
  
  props: {
    record: {
      type: Object,
      required: true,
      validator: function(obj) {
        return Object.prototype.hasOwnProperty.call(obj, 'timestamp');
      }
    }
  },

  data() {
    return {
      isExpanded: false,
      showKebabMenu: false,
      isEditing: false,
      isSaving: false,
      editingText: '',
      selectedAction: this.record.messageAction || 'RECORD_RESPONSE',
      previousRadioAction: this.record.messageAction || 'RECORD_RESPONSE',
      showPhoneDialog: false,
      phoneDialogValue: '',
      phoneValidationError: false,
      actionLabels: {
        RECORD_RESPONSE: ". . . and record the detainee's response.",
        PLAY_ONLY: '. . . and do nothing else and hang up.',
        FORWARD_CALL: '. . . and forward the call to this number: '
      }
    }
  },

  methods: {
    formatDetailedTimestamp(timestamp) {
      if (!timestamp) return '';
      const date = new Date(timestamp);
      return date.toLocaleString('en-US', {
        hour: 'numeric',
        minute: 'numeric',
        weekday: 'short',
        month: 'short',
        day: 'numeric'
      });
    },

    toggleExpanded() {
      this.isExpanded = !this.isExpanded;
      this.showKebabMenu = false;
    },

    toggleKebabMenu(event) {
      event.stopPropagation();
      document.dispatchEvent(new Event('close-all-kebabs'));
      this.showKebabMenu = !this.showKebabMenu;
    },

    async startEdit() {
      if (this.record.deliveryDate) {
        return;
      }
      this.isEditing = true;
      this.editingText = this.record.message["en-US"] || '';
      await this.$nextTick();
      this.$refs.messageInput?.focus();
    },

    async saveMessage() {
      if (this.isSaving) return; // Prevent double-clicks
      
      this.isSaving = true;
      
      try {
        const messageObject = {
          message: {
            "en-US": this.editingText,
            "es-MX": "",
            "ru-RU": ""
          }
        };
        
        const response = await axios.put(
          `/portaldb/db/translateAndUpdateDetaineeCallback/${this.record.parentCallSid}/${this.record.recordId}`, 
          messageObject
        );

        if (response.data.success) {
          // Check if translation failed
          if (response.data.translationFailed) {
            // Show error dialog
            alert("The English was saved but we failed to translate properly for Spanish and Russian. This is probably not a problem.");
          }
          
          this.isEditing = false;
          this.editingText = '';
        }
        
      } catch (error) {
        console.error('Error saving message:', error);
        alert("Failed to save the message. Please try again.");
      } finally {
        this.isSaving = false;
      }
    },

    cancelEdit() {
      this.isEditing = false;
      this.editingText = '';
    },

    async handleActionChange(newValue) {
      if (this.record.deliveryDate) {
        return;
      }

      // Step 1: selectedAction is already newValue due to Vue's reactivity
      // previousRadioAction should already contain the last confirmed state

      // Step 2: Handle FORWARD_CALL logic
      if (newValue === 'FORWARD_CALL') {
        if (!this.isValidPhoneNumber(this.record.detaineeForwardNumber)) {
          // No valid phone number, show dialog (selectedAction stays as FORWARD_CALL - "pending")
          this.openPhoneDialog();
          return;
        }
        // Valid phone number exists, proceed with normal update
      }

      // Step 3: Persist to database for all cases (including FORWARD_CALL with valid phone)
      try {
        const updateObject = {
          ...this.record,
          messageAction: newValue
        };
        
        const response = await axios.put(
          `/portaldb/db/updateDetaineeCallbackRecord/${this.record.parentCallSid}/${this.record.recordId}`, 
          updateObject
        );

        if (response.data.success) {
          this.$emit('update:record', updateObject);
          // Update previousRadioAction only on successful persist
          this.previousRadioAction = newValue;
        } else {
          // Restore previous state on failure
          this.selectedAction = this.previousRadioAction;
        }
      } catch (error) {
        console.error('Error updating action:', error);
        // Restore previous state on error
        this.selectedAction = this.previousRadioAction;
      }
    },

    openPhoneDialog() {
      if (this.record.deliveryDate) {
        return;
      }
      
      // Don't open dialog if already open
      if (this.showPhoneDialog) {
        return;
      }
      
      // Format the phone number for display in the dialog
      this.phoneDialogValue = this.formatPhoneNumber(this.record.detaineeForwardNumber) || '';
      this.phoneValidationError = false;
      this.showPhoneDialog = true;
    },

    async validateAndSavePhone() {
      const cleanedNumber = this.cleanPhoneNumber(this.phoneDialogValue);
      
      if (!cleanedNumber || cleanedNumber.length !== 10) {
        this.phoneValidationError = true;
        return;
      }

      this.phoneValidationError = false;
      
      try {
        const updateObject = {
          ...this.record,
          messageAction: 'FORWARD_CALL',
          detaineeForwardNumber: `+1${cleanedNumber}`
        };
        
        const response = await axios.put(
          `/portaldb/db/updateDetaineeCallbackRecord/${this.record.parentCallSid}/${this.record.recordId}`, 
          updateObject
        );

        if (response.data.success) {
          this.$emit('update:record', updateObject);
          // Update previousRadioAction only on successful persist
          this.previousRadioAction = 'FORWARD_CALL';
          this.showPhoneDialog = false;
        } else {
          // Restore previous state on failure
          this.selectedAction = this.previousRadioAction;
          this.showPhoneDialog = false;
        }
      } catch (error) {
        console.error('Error saving phone number:', error);
        // Restore previous state on error
        this.selectedAction = this.previousRadioAction;
        this.showPhoneDialog = false;
      }
    },

    cancelPhoneDialog() {
      this.selectedAction = this.previousRadioAction;
      this.phoneValidationError = false;
      this.showPhoneDialog = false;
    },

    cleanPhoneNumber(input) {
      if (!input) return '';
      
      // Remove everything after 'x', 'ext', 'extension' (case insensitive)
      const withoutExt = input.replace(/\s*(x|ext|extension).*$/i, '');
      
      // Extract only digits
      const digits = withoutExt.replace(/\D/g, '');
      
      // Take rightmost 10 digits
      return digits.slice(-10);
    },

    formatPhoneNumber(phoneNumber) {
      if (!phoneNumber) return '';
      
      // Remove +1 prefix and any non-digits
      const digits = phoneNumber.replace(/^\+1/, '').replace(/\D/g, '');
      
      if (digits.length === 10) {
        return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
      }
      
      return phoneNumber;
    },

    isValidPhoneNumber(phoneNumber) {
      if (!phoneNumber) return false;
      const cleaned = this.cleanPhoneNumber(phoneNumber);
      return cleaned.length === 10;
    },

    async toggleAlert(event) {
      event.stopPropagation();
      try {
        if (!this.record.parentCallSid || !this.record.recordId) {
          console.error('Missing parentCallSid or recordId');
          return;
        }

        const updateObject = {
          ...this.record,
          showAlert: !this.record.showAlert
        };
        
        const response = await axios.put(
          `/portaldb/db/updateDetaineeCallbackRecord/${this.record.parentCallSid}/${this.record.recordId}`, 
          updateObject
        );

        if (response.data.success) {
          this.$emit('update:record', updateObject);
        }
        
        this.showKebabMenu = false;
      } catch (error) {
        console.error('Error toggling alert:', error);
      }
    },

    async toggleFlag(event) {
      event.stopPropagation();
      try {
        if (!this.record.parentCallSid || !this.record.recordId) {
          console.error('Missing parentCallSid or recordId');
          return;
        }

        const updateObject = {
          ...this.record,
          flag: !this.record.flag
        };
        
        const response = await axios.put(
          `/portaldb/db/updateDetaineeCallbackRecord/${this.record.parentCallSid}/${this.record.recordId}`, 
          updateObject
        );

        if (response.data.success) {
          this.$emit('update:record', updateObject);
        }
        
        this.showKebabMenu = false;
      } catch (error) {
        console.error('Error toggling flag:', error);
      }
    },

    async dismissAlert(event) {
      event.stopPropagation();
      try {
        if (!this.record.parentCallSid || !this.record.recordId) {
          console.error('Missing parentCallSid or recordId');
          return;
        }

        const updateObject = {
          ...this.record,
          showAlert: false,
          flag: false
        };
        
        const response = await axios.put(
          `/portaldb/db/updateDetaineeCallbackRecord/${this.record.parentCallSid}/${this.record.recordId}`, 
          updateObject
        );

        if (response.data.success) {
          this.$emit('update:record', updateObject);
        }
      } catch (error) {
        console.error('Error dismissing alert:', error);
      }
    },

    async checkAndTranslateDetaineeMessage() {
      // Check if we need to translate
      if (!this.record.detaineeMessage) {
        return; // No detaineeMessage to translate
      }

      const detaineeMessage = this.record.detaineeMessage;
      
      // If English already exists, do nothing
      if (detaineeMessage["en-US"] && detaineeMessage["en-US"].trim() !== "") {
        return;
      }

      // Check for Russian text first
      let textToTranslate = null;
      let sourceLanguage = null;

      if (detaineeMessage["ru-RU"] && detaineeMessage["ru-RU"].trim() !== "") {
        textToTranslate = detaineeMessage["ru-RU"];
        sourceLanguage = "Russian";
      } else if (detaineeMessage["es-MX"] && detaineeMessage["es-MX"].trim() !== "") {
        textToTranslate = detaineeMessage["es-MX"];
        sourceLanguage = "Spanish";
      }

      // If no text to translate, do nothing
      if (!textToTranslate) {
        return;
      }

      try {
        const response = await axios.post('/portaldb/db/oneWayTranslate', {
          text: textToTranslate,
          inputLanguage: sourceLanguage,
          parentCallSid: this.record.parentCallSid,
          recordId: this.record.recordId
        });

        if (response.data.success) {
          // Update the local record with the translated text
          const updatedRecord = {
            ...this.record,
            detaineeMessage: {
              ...this.record.detaineeMessage,
              "en-US": response.data.data
            }
          };
          
          // Emit the updated record to parent component
          this.$emit('update:record', updatedRecord);
        } else {
          this.showTranslationError();
        }

      } catch (error) {
        console.error('Translation error:', error);
        this.showTranslationError();
      }
    },

    showTranslationError() {
      const errorMessage = `There was an error in /oneWayTranslate for ${this.record.parentCallSid} for recordId ${this.record.recordId}. Please copy and paste this error and email it to support@BailBondAgentServices.com`;
      alert(errorMessage);
    }
  },

  async mounted() {
    const handleOutsideClick = (event) => {
      if (this.showKebabMenu && !event.target.closest('.kebab-menu-dropdown') && !event.target.closest('.kebab-menu')) {
        this.showKebabMenu = false;
      }
    };

    const handleEscKey = (event) => {
      if (event.key === 'Escape' && this.showKebabMenu) {
        this.showKebabMenu = false;
      }
    };

    const handleCloseKebabs = () => this.showKebabMenu = false;

    document.addEventListener('click', handleOutsideClick);
    document.addEventListener('keydown', handleEscKey);
    document.addEventListener('close-all-kebabs', handleCloseKebabs);

    // Add the translation check
    await this.checkAndTranslateDetaineeMessage();

    this.$options.unmounted = () => {
      document.removeEventListener('click', handleOutsideClick);
      document.removeEventListener('keydown', handleEscKey);
      document.removeEventListener('close-all-kebabs', handleCloseKebabs);
    };
  }
}

</script>

<style scoped>

.spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.child-record {
  border: 1px solid #d2b48c;
  margin-bottom: 8px;
  border-radius: 4px;
}

.child-record-header {
  background-color: #f8f8f0;
  padding-left: 4px; 
  cursor: pointer;
  display: flex;
  align-items: center;
  position: relative;
}

.arrow-button {
  background: none;
  border: none;
  color: #8b4513;
  font-size: 18px;
  margin-right: 4px;
  cursor: pointer;
  user-select: none;
  -webkit-user-select: none;
  transform: scale(2.0);
  transform-origin: center;
  line-height: 1;
}

.record-type {
  font-weight: 500;
  margin-right: 10px;
}

.timestamps {
  flex-grow: 1;
  margin-left: auto; 
  margin-right: 4px;
  text-align: right; 
  display: flex;
  flex-direction: column;
  align-items: flex-end; 
  justify-content: flex-start;  
}

.timestamp-line {
  color: gray; 
  font-size: 0.85em;
  line-height: 1.2;
  margin: 0;
}

.flag-icon {
  color: #dc3545;
  font-size: 24px;
  margin: 0 8px;
  display: flex;
  align-items: center;
}

.alert-message {
  background-color: #e5cdac;
  border: 1px solid #c4a681;
  border-radius: 8px;
  padding: 4px 8px;
  display: flex;
  align-items: center;
  position: absolute;
  left: 32%; 
}

.alert-message.status-completed {
  background-color: #dc3545;
}

.alert-text {
  color: #ffffff;
  margin-right: 8px;
  font-weight: bold;
}

.alert-dismiss {
  color: #ffffff;
  cursor: pointer;
  font-size: 1.2em;
  padding: 0 4px;
}

.kebab-menu {
  background: none;
  outline: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  color: #8b4513;
  padding: 8px 12px;
  margin-left: auto;
}

.kebab-menu:focus {
  outline: none;
}

.kebab-menu-dropdown {
  position: absolute;
  right: 0;
  top: 100%;
  background-color: white;
  border: 1px solid #d2b48c;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  z-index: 100;
  min-width: 120px;
}

.kebab-menu-dropdown button {
  display: block;
  width: 100%;
  text-align: left;
  padding: 8px 12px;
  background: none;
  border: none;
  cursor: pointer;
}

.kebab-menu-dropdown button:hover {
  background-color: #f5f5dc;
}

.expanded-content {
  padding: 16px;
  background-color: white;
}

.message-section {
  margin-bottom: 16px;
}

.message-text {
  min-height: 120px;
  white-space: pre-wrap;
  border: 1px solid rgba(0, 0, 0, 0.38);
  border-radius: 4px;
  padding: 12px;
  width: 100%;
  background-color: white;
}

.edit-icon {
  opacity: 0.3;
  transition: opacity 0.2s ease;
}

.edit-icon:hover {
  opacity: 1;
}

:deep(.v-textarea .v-field__input) {
  min-height: 120px !important;
  white-space: pre-wrap;
  width: 100%;
}

.message-action {
  margin-top: 16px;
  font-weight: 500;
}

.response-text {
  margin-top: 12px;
  line-height: 1.4;
}

.recording-section {
  margin-top: 16px;
  padding: 0.75rem;
  border-radius: 0.5rem;
}

.audio-player {
  width: 100%;
  height: 32px;
  max-width: 500px;
}

.phone-input {
  max-width: 140px !important;
  min-width: 140px !important;
}

.phone-input :deep(.v-field__input) {
  cursor: pointer;
}

.phone-input :deep(.v-field--disabled .v-field__input) {
  cursor: not-allowed;
}

.forward-call-label {
  margin-top: 4px;
  word-break: normal !important;
}

.forward-call-container {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

@media (max-width: 600px) {

  .forward-call-container {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .forward-call-container .phone-input {
    margin-left: 0 !important;
    margin-top: 8px;
    width: 100%;
  }
  
  .timestamps {
    font-size: 0.85em;
  }
  
  .alert-message {
    font-size: 0.85em;
    padding: 2px 6px;
    left: 20%;
    margin-right:8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
  }

  .expanded-content {
    padding: 12px;
  }

  .audio-player {
    width: 100%;
    max-width: 100%;
  }

  .recording-section {
    padding: 0.5rem;
  }

  .flag-icon {
    margin: -6px 3px -15px 0px;
  }
}
</style>
